import { useState } from 'react';

import { EscalationDetail } from './EscalationDetail';
import { EscalationList } from './EscalationList';
import styles from './EscalationScreen.module.css';

/**
 * The escalations screen (M-10, Task 5) — `/escalations`, a nav entry that
 * has never pointed anywhere before this milestone (spec §0/§3: the legacy
 * hid the whole feature by commenting out its own sidebar links, and
 * "the nav entry ships" is spec §0's own second consequence). Reachable
 * only for a session holding `escalation.view` (`App.tsx`'s own
 * `rendersEscalations`, mirroring `rendersRequests`).
 *
 * ## The list is never unmounted to show a detail
 *
 * The identical shape `CaseScreen.tsx`'s own docblock explains at length: an
 * early return that swaps `<EscalationList>` for `<EscalationDetail>`
 * destroys the list's own `draftFilters`/`appliedFilters`/`cursor` state the
 * moment a row opens, so `onBack` would come back to a freshly-mounted,
 * unfiltered list. The list stays mounted in a `hidden` div; opening a row
 * only adds `<EscalationDetail>` as a sibling.
 *
 * ## The `hidden` div carries NO `className` (whole-branch review, C-2)
 *
 * An author-origin `display` declaration beats the UA stylesheet's
 * `[hidden] { display: none }` — cascade ORIGIN wins before specificity is
 * even considered — so putting `styles['listPage']` (`display: flex`)
 * directly on the `hidden` element defeats `hidden` entirely: the queue,
 * its filters, and the routing panel stay on screen, stacked below the
 * opened detail. `CaseScreen.tsx`, the pattern this file copies, uses a
 * BARE `hidden` div for exactly this reason. The `listPage` layout (the
 * gap between `EscalationList` and `RouteSettings`) now lives on a nested
 * div INSIDE the bare `hidden` one, so it never fights the attribute that
 * hides it.
 *
 * ## The routing panel is NOT here
 *
 * `RouteSettings` used to sit under the list. It moved to Admin settings
 * (`/admin-settings`), under an Escalations heading: configuring the chain is
 * administration, not queue work.
 */

export interface EscalationScreenProps {
  permissions: readonly string[];
}

export function EscalationScreen({ permissions }: EscalationScreenProps) {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <div className={styles['page']}>
      {selected === null ? null : (
        <EscalationDetail
          uuid={selected}
          permissions={permissions}
          onBack={() => {
            setSelected(null);
          }}
        />
      )}

      {/* `hidden`, never a ternary that unmounts — see this file's own
          docblock. Bare — no `className` here; the `listPage` layout lives
          on the nested div below, not on this one (C-2). */}
      <div hidden={selected !== null}>
        <div className={styles['listPage']}>
          <EscalationList
            permissions={permissions}
            active={selected === null}
            onOpen={(uuid) => {
              setSelected(uuid);
            }}
          />
        </div>
      </div>
    </div>
  );
}
